/**
 * Global Compose Drawer Events
 *
 * Lightweight event emitter that allows any component (e.g. ClientMessagesHub)
 * to open the Layout-level compose drawers (Email, SMS, In-App Message) with
 * optional pre-populated client data.
 *
 * Usage:
 *   // Trigger from anywhere:
 *   composeEvents.emit('email', { clientId: '...', clientName: '...' })
 *   composeEvents.emit('sms',   { clientId: '...', clientName: '...' })
 *   composeEvents.emit('message', { clientId: '...', clientName: '...' })
 *
 *   // Listen in Layout:
 *   useComposeListener((type, client) => { ... })
 */

import { useEffect } from 'react'

type ComposeType = 'email' | 'sms' | 'message'

interface ComposeClient {
  clientId: string
  clientName?: string
  email?: string
  phone?: string
}

type ComposeHandler = (type: ComposeType, client?: ComposeClient) => void

const listeners = new Set<ComposeHandler>()

export const composeEvents = {
  emit(type: ComposeType, client?: ComposeClient) {
    listeners.forEach(fn => fn(type, client))
  },
  subscribe(handler: ComposeHandler) {
    listeners.add(handler)
    return () => { listeners.delete(handler) }
  },
}

/**
 * Hook to listen for compose events in a component (typically Layout).
 */
export function useComposeListener(handler: ComposeHandler) {
  useEffect(() => {
    return composeEvents.subscribe(handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
